import { Component, contentChildren, effect, output } from '@angular/core';
import { merge, Subscription } from 'rxjs';
import { ChildTabComponent } from './child-tab/child-tab.component';

/**
 * A simplified version of the tab-bar for navigation between pages.
 * Meant to be a child component that can provide state changes without the route navigation changes
 */
@Component({
    selector: 'app-child-tab-bar',
    host: { role: 'tablist' },
    template: `<ng-content select="app-child-tab" />`,
    styleUrl: './child-tab-bar.style.scss',
    standalone: true,
})
export class ChildTabBarComponent {
    tabs = contentChildren(ChildTabComponent);

    /**
     * Subscribes to all of the child tabs rendered in this parent component.
     * This allows for easy communication of which tab is currently active
     */
    subscribeToChildren = effect(
        (onCleanup) => {
            const tabs = this.tabs();
            if (tabs.length) {
                // The effect is re-run when the children within change, in this case, any old subscriptions should be cleared out
                if (this._subscription) {
                    this._subscription.unsubscribe();
                }
                // When a tab has been clicked, output the newly active ID and update each child to determine whether they are active or not
                this._subscription = merge(...tabs.map((tab) => tab.onClick)).subscribe((activeId) => {
                    this.onTabChange.emit(activeId);
                    tabs.forEach((tab) => tab.currentlyActiveId.set(activeId));
                });
            }

            onCleanup(() => this._subscription?.unsubscribe());
        },
        { allowSignalWrites: true }
    );

    /**
     * Emits the ID of the newly activated tab
     */
    onTabChange = output<string>();

    private _subscription?: Subscription;
}
